/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import {
  KpiCard, DateStepper, SegmentedPills, PrimaryBtn, SecondaryBtn, StatusPill,
  Toast, useToastAutoDismiss,
  rangeForPeriod, priorPeriodRange,
} from './components/systemKit';
import { isItemUsable } from './lib/parseGuardrails';
import { csvCell } from './lib/csv';
import PremiumLineChart from './components/PremiumLineChart';
import InvoiceDetailDrawer from './components/InvoiceDetailDrawer';
import InvoiceReviewItemDrillIn from './components/InvoiceReviewItemDrillIn';
import TopReports from './components/costDashboard/TopReports';
import { navigate } from './lib/navigate';

// ─── Invoices v2 ────────────────────────────────────────────────────────────
// Operator's home for everything invoice-related. Two zones:
//   1. Invoice browser at the top — find/scrub specific invoices, click
//      into a detail drawer with summary + line items + vendor comparison.
//   2. Analytics below — period-comparison sections (8 originals + 3 new)
//      OR a single-item history report (toggleable via SegmentedPills).
//
// Renamed from Reports → Invoices in this commit. Tab key, sidebar label,
// page title, and CSS data attributes all updated. The 8 original analytics
// sections survived as the "Period summary" mode; new sections (Spend by
// vendor / Top 20 items by spend / Dollar-impact movers) added per
// operator feedback ("If you can combine every Invoice you have for a year,
// what would you want to see?").
//
// LANDS PRE-FILLED: when the operator clicks "Open invoices →" from the
// Inventory Trends compact strip, App.js sets a one-shot navConfig with
// `{ period, anchor }` that we consume on mount and clear via
// onConsumeNavConfig() so re-navigating doesn't re-trigger.
//
// See DESIGN_SYSTEM.md §Invoices surface.

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtIsoDate(d) {
  return d.toISOString().split('T')[0];
}

function fmtMoney(n) {
  if (n == null || !isFinite(n)) return '$0';
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function fmtMoneyExact(n) {
  if (n == null || !isFinite(n)) return '$0.00';
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtNum(n) {
  if (n == null || !isFinite(n)) return '0';
  return Number(n).toLocaleString('en-US');
}

function fmtDelta(pct) {
  if (pct == null || !isFinite(pct)) return '\u2014';
  const sign = pct >= 0 ? '\u2191' : '\u2193';
  return `${sign} ${Math.abs(pct).toFixed(1)}%`;
}

function deltaPct(curr, prior) {
  if (prior == null || prior === 0) return null;
  return ((curr - prior) / prior) * 100;
}

function deltaTone(pct) {
  if (pct == null) return null;
  if (Math.abs(pct) < 0.5) return 'neutral';
  return pct >= 0 ? 'success' : 'critical';
}

function deltaToneSpend(pct) {
  // For SPEND, "up" is bad (cost is rising) — invert tone.
  if (pct == null) return null;
  if (Math.abs(pct) < 0.5) return 'neutral';
  return pct >= 0 ? 'critical' : 'success';
}

function fmtDateShort(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function fmtVendor(v) {
  return v ? String(v).replace(/\n/g, ' \u00B7 ') : '\u2014';
}

// formatRangeLabel — period-appropriate string for headings + filenames.
//   week     → "Apr 19 – Apr 25, 2026"
//   month    → "April 2026"
//   quarter  → "Q2 2026"
//   year     → "2026"
function formatRangeLabel(period, range) {
  const start = new Date(range.start + 'T00:00:00');
  if (period === 'quarter') {
    const q = Math.floor(start.getMonth() / 3) + 1;
    return `Q${q} ${start.getFullYear()}`;
  }
  if (period === 'month') return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  if (period === 'year') return String(start.getFullYear());
  const end = new Date(range.end + 'T00:00:00');
  return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

// "Same period last year" — shift range start/end back by 1 year.
function sameLastYear(range) {
  const start = new Date(range.start + 'T00:00:00');
  const end = new Date(range.end + 'T00:00:00');
  start.setFullYear(start.getFullYear() - 1);
  end.setFullYear(end.getFullYear() - 1);
  return { start: fmtIsoDate(start), end: fmtIsoDate(end) };
}

// Slug a label for use in a filename. "Q2 2026" → "Q2-2026".
function slug(label) {
  return String(label).replace(/\s+/g, '-').replace(/[^A-Za-z0-9-]/g, '');
}

// slugifyItemName — strips Posted's item-name conventions for a clean
// filename slug. Transformation rules (documented per user request):
//   1. Drop leading dash(es)        "-BEEF" → "BEEF"
//   2. Drop parenthetical content   "BEEF (case)" → "BEEF"
//   3. Lowercase                    "BEEF" → "beef"
//   4. Hyphenate non-alphanumeric   "beef chuck" → "beef-chuck"
//   5. Trim leading/trailing dashes
// Example:
//   "-BEEF CHUCK SHOULDER CLOD CHOICE (case)" → "beef-chuck-shoulder-clod-choice"
function slugifyItemName(name) {
  return String(name || 'item')
    .replace(/^-+/, '')
    .replace(/\s*\([^)]*\)/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'item';
}

// Browser zone date-range presets. "All time" = unbounded (we still order DESC
// and paginate, so it stays performant). "Custom" opens a DateStepper.
const BROWSER_RANGE_PRESETS = [
  { key: 'last30',   label: 'Last 30 days',  daysBack: 30  },
  { key: 'last90',   label: 'Last 90 days',  daysBack: 90  },
  { key: 'lastYear', label: 'Last year',     daysBack: 365 },
  { key: 'all',      label: 'All time',      daysBack: null },
];

function browserRangeFromPreset(presetKey) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const preset = BROWSER_RANGE_PRESETS.find(p => p.key === presetKey);
  if (!preset || preset.daysBack == null) return { start: null, end: null };
  const start = new Date(today); start.setDate(today.getDate() - preset.daysBack + 1);
  return { start: fmtIsoDate(start), end: fmtIsoDate(today) };
}

// CSV cell escape moved to ./lib/csv.js — same helper now serves the
// Period Summary, Single Item History, Top Spend, and Top Movers exports.

// Bucket lines into ISO weeks (Sunday-anchored) within the [range] for the
// quarter/year weekly breakdown. Returns an array of { weekStart, weekEnd,
// label, lines[] }.
function bucketByWeek(lines, range) {
  const weeks = [];
  const start = new Date(range.start + 'T00:00:00');
  const end = new Date(range.end + 'T00:00:00');
  // Snap weekStart back to the Sunday of the range's first week.
  const cursor = new Date(start);
  cursor.setDate(cursor.getDate() - cursor.getDay());
  while (cursor <= end) {
    const wsIso = fmtIsoDate(cursor);
    const we = new Date(cursor); we.setDate(we.getDate() + 6);
    const weIso = fmtIsoDate(we);
    weeks.push({
      weekStart: wsIso,
      weekEnd: weIso,
      label: cursor.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      lines: [],
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  for (const l of lines) {
    const w = weeks.find(w => l.invoice_date >= w.weekStart && l.invoice_date <= w.weekEnd);
    if (w) w.lines.push(l);
  }
  return weeks;
}

// ── Component ──────────────────────────────────────────────────────────────

export default function Invoices({ companyId, navConfig, onConsumeNavConfig }) {
  // ── Period summary mode (existing behavior) ─────────────────────────────
  const [period, setPeriod] = useState('quarter');
  const [range, setRange] = useState(() => rangeForPeriod('quarter', new Date()));
  const [comparison, setComparison] = useState('prior'); // 'prior' | 'sameLastYear' | 'custom'
  const [customRange, setCustomRange] = useState(() => priorPeriodRange('quarter', rangeForPeriod('quarter', new Date())));
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [staleConfig, setStaleConfig] = useState(false);

  // ── Page tabs (Browse | Reports) ────────────────────────────────────────
  // Persisted per-user via cost_dashboard_prefs.invoices_active_tab so the
  // operator returns to where they left off. Default 'browse' on first
  // visit. Persistence is fire-and-forget (no await) so tab clicks feel
  // instant; if it fails the next click overwrites.
  const [activeTab, setActiveTab] = useState('browse');  // 'browse' | 'reports'
  const userIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      userIdRef.current = user.id;
      const { data: prefs } = await supabase
        .from('cost_dashboard_prefs')
        .select('invoices_active_tab')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      if (prefs?.invoices_active_tab) setActiveTab(prefs.invoices_active_tab);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (userIdRef.current) {
      supabase.from('cost_dashboard_prefs').upsert({
        user_id: userIdRef.current,
        invoices_active_tab: tab,
      });
    }
  };

  // ── Report mode toggle (Period summary | Single item history | Top spend | Top movers) ──
  // Top spend and Top movers added in this commit. Their controls + result
  // panels live in <TopReports>; they reuse the cost_dashboard_top_spend
  // and cost_dashboard_top_impact_movers RPCs that already power the Cost
  // Dashboard.
  const [reportMode, setReportMode] = useState('summary'); // 'summary' | 'item' | 'top-spend' | 'top-movers'

  // Drill-in slide-over (used by Top spend / Top movers report rows).
  // Owned at this level so any report row can open it. ESC handled here.
  const [drillIn, setDrillIn] = useState(null);
  useEffect(() => {
    if (!drillIn) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrillIn(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drillIn]);

  const openDrillInForRow = async (row) => {
    if (!row?.food_item_id) return;
    const { data } = await supabase.from('invoice_line_items')
      .select('id, invoice_id, invoice_date, parse_diff')
      .eq('company_id', companyId)
      .eq('food_item_id', row.food_item_id)
      .order('invoice_date', { ascending: false })
      .limit(1);
    const latest = data?.[0];
    if (!latest) return;
    setDrillIn({
      foodItemId: row.food_item_id,
      itemName: row.item_name,
      isCatchWeight: !!row.is_catch_weight,
      currentInvoiceId: latest.invoice_id,
      currentInvoiceDate: latest.invoice_date,
      parseDiff: latest.parse_diff || null,
    });
  };

  // ── Single item history mode ────────────────────────────────────────────
  // Default period: 'year' (per-item history is most valuable as long-range).
  // Range aligns to the current calendar year so the DateStepper label
  // ("2026") matches what the range covers. Operators can zoom in to
  // Quarter/Month or step back via arrows.
  const [itemPeriod, setItemPeriod] = useState('year');
  const [itemRange, setItemRange] = useState(() => rangeForPeriod('year', new Date()));
  const [itemSelected, setItemSelected] = useState(null); // food_items row
  const [itemSearch, setItemSearch] = useState('');
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [itemAllItems, setItemAllItems] = useState([]); // catalog for picker
  const [itemHistoryReport, setItemHistoryReport] = useState(null);
  const [itemHistoryRunning, setItemHistoryRunning] = useState(false);
  const [itemChartMetric, setItemChartMetric] = useState('perLb'); // 'perLb' | 'perCase' | 'volume'

  // ── Invoice browser zone ────────────────────────────────────────────────
  const [browserPreset, setBrowserPreset] = useState('last90');
  const [browserCustomRange, setBrowserCustomRange] = useState(() => browserRangeFromPreset('last90'));
  const [browserVendor, setBrowserVendor] = useState('');
  const [browserSearch, setBrowserSearch] = useState('');
  // Review-status filter pills. Defaults to 'unreviewed' on mount IFF
  // the operator has any pending invoices (set by an effect below);
  // otherwise 'all'. See DESIGN_SYSTEM.md §Live notification surfaces.
  const [browserReviewFilter, setBrowserReviewFilter] = useState('all');
  const [browserPendingCount, setBrowserPendingCount] = useState(0);
  const [browserSearchDebounced, setBrowserSearchDebounced] = useState('');
  const [browserInvoices, setBrowserInvoices] = useState([]);
  const [browserLineMeta, setBrowserLineMeta] = useState({}); // { [invoiceId]: { count, total } }
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserHasMore, setBrowserHasMore] = useState(false);
  const [browserDistinctVendors, setBrowserDistinctVendors] = useState([]);
  const [browserPage, setBrowserPage] = useState(0);
  const [drawerInvoice, setDrawerInvoice] = useState(null);

  // ── Parser toast ────────────────────────────────────────────────────────
  // Migrated to systemKit.Toast utility. Auto-dismiss now 3s (was 4s) per the
  // standardized convention introduced with the Recipe Health surface — see
  // DESIGN_SYSTEM.md §Toast on every state transition.
  const [parserToast, setParserToast] = useState(null); // { kind, text }
  useToastAutoDismiss(parserToast, setParserToast);

  // navConfig pre-fill on mount — one-shot, then cleared via the App-level
  // setter so navigating away and back doesn't re-trigger.
  useEffect(() => {
    if (navConfig?.period && navConfig?.anchor) {
      const nextPeriod = navConfig.period;
      const anchor = new Date(navConfig.anchor + 'T00:00:00');
      setPeriod(nextPeriod);
      setRange(rangeForPeriod(nextPeriod, anchor));
      setCustomRange(priorPeriodRange(nextPeriod, rangeForPeriod(nextPeriod, anchor)));
      onConsumeNavConfig?.();
    }
  }, []);

  // Mark report stale when config changes after a successful run. Display-only
  // hint; we don't auto-rerun (would spam DB on every config tweak).
  useEffect(() => {
    if (report) setStaleConfig(true);
  }, [period, range.start, range.end, comparison, customRange.start, customRange.end]);

  // ── Browser zone: distinct vendors + items (for filter dropdowns) ──────
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      const [invRes, itemsRes] = await Promise.all([
        supabase.from('invoices').select('vendor_name')
          .eq('company_id', companyId)
          .range(0, 49999),
        supabase.from('food_items')
          .select('id, normalized_name, category, is_catch_weight, needs_review')
          .eq('company_id', companyId)
          .order('normalized_name', { ascending: true })
          .range(0, 9999),
      ]);
      if (cancelled) return;
      const vendors = Array.from(new Set((invRes.data || []).map(r => r.vendor_name).filter(Boolean))).sort();
      setBrowserDistinctVendors(vendors);
      setItemAllItems(itemsRes.data || []);
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // ── Browser zone: debounce search input (200ms) ─────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setBrowserSearchDebounced(browserSearch.trim()), 200);
    return () => clearTimeout(t);
  }, [browserSearch]);

  // ── Browser zone: derived range from preset (or custom) ─────────────────
  const browserRange = useMemo(() => {
    if (browserPreset === 'custom') return browserCustomRange;
    return browserRangeFromPreset(browserPreset);
  }, [browserPreset, browserCustomRange]);

  // ── Browser zone: load invoice list (paginated, 25/page) ────────────────
  // Reset page + list whenever filters change.
  useEffect(() => {
    setBrowserPage(0);
    setBrowserInvoices([]);
    setBrowserLineMeta({});
    setBrowserHasMore(false);
  }, [browserPreset, browserCustomRange.start, browserCustomRange.end, browserVendor, browserSearchDebounced, browserReviewFilter]);

  // Pending-invoice count for the review-filter pill. Single round-trip,
  // refreshed when companyId changes or after the operator visits the
  // Invoice Review modal (the modal updates review_status, which the
  // sidebar realtime push doesn't see locally — Invoices is its own page
  // and re-fetches on mount).
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('invoices')
        .select('id')
        .eq('company_id', companyId)
        .eq('review_status', 'pending');
      if (cancelled) return;
      setBrowserPendingCount(data?.length || 0);
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  // Smart default: if the operator lands on Invoices and there are
  // pending parses to review, pre-select the Unreviewed pill so the
  // first thing they see is what needs attention. Only applies on first
  // mount per the count fetch — they can pivot to All freely.
  const browserReviewFilterPrimedRef = useRef(false);
  useEffect(() => {
    if (browserReviewFilterPrimedRef.current) return;
    if (browserPendingCount > 0) {
      setBrowserReviewFilter('unreviewed');
      browserReviewFilterPrimedRef.current = true;
    } else if (browserPendingCount === 0) {
      // Lock the default in once we've confirmed there's nothing pending,
      // so a later "Acknowledge" doesn't flip the pill back.
      browserReviewFilterPrimedRef.current = true;
    }
  }, [browserPendingCount]);

  // Fetch the active page whenever filters change OR Load more is clicked.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setBrowserLoading(true);
      const PAGE_SIZE = 25;
      const offset = browserPage * PAGE_SIZE;
      let q = supabase.from('invoices')
        .select('id, invoice_date, vendor_name, invoice_number, total_amount, file_url, status, review_status, parse_summary, parse_summary_at, source_type')
        .eq('company_id', companyId)
        .order('invoice_date', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + PAGE_SIZE); // request 26 to detect hasMore
      if (browserVendor) q = q.eq('vendor_name', browserVendor);
      if (browserRange.start) q = q.gte('invoice_date', browserRange.start);
      if (browserRange.end) q = q.lte('invoice_date', browserRange.end);
      if (browserReviewFilter === 'unreviewed') q = q.eq('review_status', 'pending');
      else if (browserReviewFilter === 'acknowledged') q = q.eq('review_status', 'acknowledged');
      else if (browserReviewFilter === 'dismissed') q = q.eq('review_status', 'dismissed');
      if (browserSearchDebounced) {
        // Match invoice_number OR vendor_name (case-insensitive).
        const escaped = browserSearchDebounced.replace(/[%,]/g, '');
        q = q.or(`invoice_number.ilike.%${escaped}%,vendor_name.ilike.%${escaped}%`);
      }
      const { data, error } = await q;
      if (cancelled) return;
      if (error) {
        setBrowserLoading(false);
        return;
      }
      const fetched = data || [];
      const hasMore = fetched.length > PAGE_SIZE;
      const visible = hasMore ? fetched.slice(0, PAGE_SIZE) : fetched;

      // Follow-up batched fetch for line counts on the visible invoices.
      // Lean fields only — we just want id + line_total to derive count
      // and (optionally) sum check.
      const ids = visible.map(r => r.id);
      let lineMeta = {};
      if (ids.length > 0) {
        const { data: linesData } = await supabase.from('invoice_line_items')
          .select('invoice_id, line_total')
          .in('invoice_id', ids)
          .range(0, 49999);
        for (const l of (linesData || [])) {
          if (!lineMeta[l.invoice_id]) lineMeta[l.invoice_id] = { count: 0, total: 0 };
          lineMeta[l.invoice_id].count += 1;
          lineMeta[l.invoice_id].total += Number(l.line_total) || 0;
        }
      }

      // Append (Load more) or replace (page 0).
      setBrowserInvoices(prev => browserPage === 0 ? visible : [...prev, ...visible]);
      setBrowserLineMeta(prev => browserPage === 0 ? lineMeta : { ...prev, ...lineMeta });
      setBrowserHasMore(hasMore);
      setBrowserLoading(false);
    })();
    return () => { cancelled = true; };
  }, [companyId, browserPage, browserPreset, browserCustomRange.start, browserCustomRange.end, browserVendor, browserSearchDebounced, browserRange.start, browserRange.end, browserReviewFilter]);

  // ── Single item history: run handler ───────────────────────────────────
  async function runItemHistory() {
    if (!companyId || !itemSelected) return;
    setItemHistoryRunning(true);
    const { data: lines } = await supabase.from('invoice_line_items')
      .select('id, invoice_id, invoice_date, vendor_name, quantity, unit_price, line_total, total_weight, price_per_lb, pack_count, piece_weight')
      .eq('company_id', companyId)
      .eq('food_item_id', itemSelected.id)
      .gte('invoice_date', itemRange.start)
      .lte('invoice_date', itemRange.end)
      .order('invoice_date', { ascending: false })
      .range(0, 9999);

    const ls = lines || [];
    // Summary stats
    const useWeight = !!itemSelected.is_catch_weight;
    const totalLbs = ls.reduce((s, l) => s + (Number(l.total_weight) || 0), 0);
    const totalCases = ls.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
    const totalSpend = ls.reduce((s, l) => s + (Number(l.line_total) || 0), 0);
    const perLbVals = ls.map(l => Number(l.price_per_lb)).filter(v => v > 0);
    const avgPerLb = perLbVals.length ? perLbVals.reduce((a, b) => a + b, 0) / perLbVals.length : null;
    const minPerLb = perLbVals.length ? Math.min(...perLbVals) : null;
    const maxPerLb = perLbVals.length ? Math.max(...perLbVals) : null;
    // Vendor concentration
    const vCounts = {};
    for (const l of ls) {
      if (l.vendor_name) vCounts[l.vendor_name] = (vCounts[l.vendor_name] || 0) + 1;
    }
    const vSorted = Object.entries(vCounts).sort((a, b) => b[1] - a[1]);
    const topVendor = vSorted[0] ? { name: vSorted[0][0], count: vSorted[0][1], total: ls.length } : null;
    // Distinct invoices
    const distinctInvoices = new Set(ls.map(l => l.invoice_id).filter(v => v != null)).size;

    setItemHistoryReport({
      runAt: new Date(),
      item: itemSelected,
      range: { ...itemRange },
      lines: ls,
      stats: {
        useWeight,
        invoiceCount: distinctInvoices || ls.length,
        totalLbs, totalCases, totalSpend,
        avgPerLb, minPerLb, maxPerLb,
        topVendor,
      },
    });
    setItemHistoryRunning(false);
  }

  // ── Single item history: CSV export (RAW LINES — one row per invoice
  //    line — since the focus is already a single item, raw lines are
  //    more useful than aggregated totals here). Filename slug rule
  //    documented at slugifyItemName.
  function exportItemHistoryCsv() {
    if (!itemHistoryReport) return;
    const { item, range: r, lines } = itemHistoryReport;
    const header = ['date','vendor','quantity','unit_price','line_total','total_weight','price_per_lb','pack_count','piece_weight'];
    const rows = [header.join(',')];
    for (const l of lines) {
      rows.push([
        csvCell(l.invoice_date),
        csvCell((l.vendor_name || '').replace(/\n/g, ' | ')),
        l.quantity != null ? Number(l.quantity) : '',
        l.unit_price != null ? Number(l.unit_price).toFixed(4) : '',
        l.line_total != null ? Number(l.line_total).toFixed(2) : '',
        l.total_weight != null ? Number(l.total_weight).toFixed(2) : '',
        l.price_per_lb != null ? Number(l.price_per_lb).toFixed(4) : '',
        l.pack_count != null ? Number(l.pack_count) : '',
        csvCell(l.piece_weight || ''),
      ].join(','));
    }
    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `posted-item-history-${slugifyItemName(item.normalized_name)}-${slug(`${r.start}_${r.end}`)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Derived: current comparison range from the selected mode.
  const compareRange = useMemo(() => {
    if (comparison === 'prior') return priorPeriodRange(period, range);
    if (comparison === 'sameLastYear') return sameLastYear(range);
    return customRange;
  }, [comparison, period, range, customRange]);

  // ── Run report ────────────────────────────────────────────────────────────
  async function runReport() {
    if (!companyId) return;
    setRunning(true);
    setStaleConfig(false);

    // Single batched fetch over the combined window. Inclusive bounds.
    const fetchStart = compareRange.start < range.start ? compareRange.start : range.start;
    const fetchEnd = compareRange.end > range.end ? compareRange.end : range.end;

    // Supabase default response cap is 1000 rows. A typical Quarter window
    // for company 4 already runs ~1100 lines, and Year scope much higher,
    // so we explicitly raise the cap. 50K is safe for any reasonable
    // inventory-tracking volume (a year of data ~6K lines on the test data).
    const [linesRes, itemsRes] = await Promise.all([
      supabase.from('invoice_line_items')
        .select('food_item_id, invoice_id, invoice_date, vendor_name, quantity, total_weight, line_total')
        .eq('company_id', companyId)
        .gte('invoice_date', fetchStart)
        .lte('invoice_date', fetchEnd)
        .range(0, 49999),
      supabase.from('food_items')
        .select('id, normalized_name, category, is_catch_weight, needs_review')
        .eq('company_id', companyId)
        .range(0, 9999),
    ]);

    const lines = linesRes.data || [];
    const items = itemsRes.data || [];
    const itemsById = {};
    items.forEach(it => { itemsById[it.id] = it; });

    const inCurrent = (l) => l.invoice_date >= range.start && l.invoice_date <= range.end;
    const inComparison = (l) => l.invoice_date >= compareRange.start && l.invoice_date <= compareRange.end;
    const currLines = lines.filter(inCurrent);
    const compLines = lines.filter(inComparison);

    // Top-line totals (all items, including flagged — operators want raw
    // spend totals on a printable report; the per-section breakdowns
    // exclude flagged items for analysis).
    const topline = (ls) => ({
      spend: ls.reduce((s, l) => s + (Number(l.line_total) || 0), 0),
      lbs: ls.reduce((s, l) => s + (Number(l.total_weight) || 0), 0),
      items: new Set(ls.map(l => l.food_item_id)).size,
      invoices: new Set(ls.map(l => l.invoice_id).filter(v => v != null)).size,
    });
    const currTopline = topline(currLines);
    const compTopline = topline(compLines);

    // ── By category ────────────────────────────────────────────────────────
    // Excludes flagged items per DESIGN_SYSTEM.md §Data Review queue.
    const catBuckets = {};
    const accumulateCat = (key) => (l) => {
      const item = itemsById[l.food_item_id];
      if (!item || !isItemUsable(item)) return;
      const cat = item.category || 'Uncategorized';
      if (!catBuckets[cat]) catBuckets[cat] = { category: cat, currLbs: 0, compLbs: 0, currSpend: 0, compSpend: 0 };
      catBuckets[cat][key === 'curr' ? 'currLbs' : 'compLbs'] += Number(l.total_weight) || 0;
      catBuckets[cat][key === 'curr' ? 'currSpend' : 'compSpend'] += Number(l.line_total) || 0;
    };
    currLines.forEach(accumulateCat('curr'));
    compLines.forEach(accumulateCat('comp'));
    const categoryRows = Object.values(catBuckets)
      .filter(c => c.currSpend > 0 || c.compSpend > 0)
      .sort((a, b) => b.currSpend - a.currSpend);

    // ── By vendor (used by both §By vendor breakdown and §Spend by vendor) ──
    const venBuckets = {};
    const accumulateVen = (key) => (l) => {
      if (!l.vendor_name) return;
      const v = l.vendor_name;
      if (!venBuckets[v]) venBuckets[v] = {
        vendor: v,
        currLbs: 0, compLbs: 0,
        currSpend: 0, compSpend: 0,
        currInvoices: new Set(), compInvoices: new Set(),
      };
      venBuckets[v][key === 'curr' ? 'currLbs' : 'compLbs'] += Number(l.total_weight) || 0;
      venBuckets[v][key === 'curr' ? 'currSpend' : 'compSpend'] += Number(l.line_total) || 0;
      const invSet = venBuckets[v][key === 'curr' ? 'currInvoices' : 'compInvoices'];
      if (l.invoice_id != null) invSet.add(l.invoice_id);
    };
    currLines.forEach(accumulateVen('curr'));
    compLines.forEach(accumulateVen('comp'));
    const vendorRows = Object.values(venBuckets)
      .filter(v => v.currSpend > 0 || v.compSpend > 0)
      .sort((a, b) => b.currSpend - a.currSpend);

    // §Spend by vendor — same rollup with extra fields: invoice count + share
    // of total period spend + $-change vs prior. New section per the
    // operator-feedback prompt ("vendor concentration is the first
    // business-owner question").
    const totalCurrSpend = vendorRows.reduce((s, v) => s + v.currSpend, 0);
    const vendorSpendRows = vendorRows.map(v => ({
      vendor: v.vendor,
      invoiceCount: v.currInvoices.size,
      currSpend: v.currSpend,
      compSpend: v.compSpend,
      shareOfPeriod: totalCurrSpend > 0 ? (v.currSpend / totalCurrSpend) * 100 : 0,
      // $ change kind: 'new' when no prior, 'dropped' when no current,
      // 'changed' otherwise (with computable Δ pct).
      kind: v.currSpend > 0 && v.compSpend === 0 ? 'new'
          : v.currSpend === 0 && v.compSpend > 0 ? 'dropped'
          : 'changed',
      changePct: v.compSpend > 0 ? ((v.currSpend - v.compSpend) / v.compSpend) * 100 : null,
    }));

    // ── Per-item rollup (drives biggest-movement / new / skipped / CSV) ────
    const perItem = {};
    const perItemAccum = (key) => (l) => {
      const item = itemsById[l.food_item_id];
      if (!item) return;
      const id = l.food_item_id;
      if (!perItem[id]) {
        perItem[id] = {
          id,
          name: item.normalized_name,
          category: item.category || 'Uncategorized',
          isCatchWeight: item.is_catch_weight,
          needs_review: !!item.needs_review,
          curr: { lbs: 0, cases: 0, spend: 0, invoices: new Set(), lastDate: null, vendors: new Set() },
          comp: { lbs: 0, cases: 0, spend: 0, invoices: new Set(), lastDate: null, vendors: new Set() },
        };
      }
      const bucket = perItem[id][key];
      bucket.lbs += Number(l.total_weight) || 0;
      bucket.cases += Number(l.quantity) || 0;
      bucket.spend += Number(l.line_total) || 0;
      if (l.invoice_id != null) bucket.invoices.add(l.invoice_id);
      if (l.vendor_name) bucket.vendors.add(l.vendor_name);
      if (!bucket.lastDate || l.invoice_date > bucket.lastDate) bucket.lastDate = l.invoice_date;
    };
    currLines.forEach(perItemAccum('curr'));
    compLines.forEach(perItemAccum('comp'));

    // Materialize per-item with delta calculations.
    const itemsList = Object.values(perItem).map(r => {
      const useWeight = r.isCatchWeight; // catch-weight items report on lbs; others on cases
      const currVol = useWeight ? r.curr.lbs : r.curr.cases;
      const compVol = useWeight ? r.comp.lbs : r.comp.cases;
      const volPct = compVol > 0 ? ((currVol - compVol) / compVol) * 100 : null;
      return {
        ...r,
        useWeight,
        currVol, compVol, volPct,
        currInvoiceCount: r.curr.invoices.size,
        compInvoiceCount: r.comp.invoices.size,
        currVendorList: Array.from(r.curr.vendors),
        compVendorList: Array.from(r.comp.vendors),
      };
    });

    // §Top 20 items by spend — operator's at-a-glance "where my food dollars
    // went this period." Includes flagged items (their chips render inline
    // for context — don't filter, just contextualize).
    const topItemsBySpend = itemsList
      .filter(it => it.curr.spend > 0)
      .sort((a, b) => b.curr.spend - a.curr.spend)
      .slice(0, 20);
    const topItemsTotalSpend = topItemsBySpend.reduce((s, it) => s + it.curr.spend, 0);
    const topItemsShareOfTotal = currTopline.spend > 0 ? (topItemsTotalSpend / currTopline.spend) * 100 : 0;

    // §Biggest dollar-impact movers — REPLACES the old %-based movers.
    // Owners think in dollars; the % version remains the right answer on
    // Inventory Trends (where operators want movement signal normalized
    // for size). See DESIGN_SYSTEM.md §Dollar-impact vs percentage movers.
    // Comparable items only (both periods > 0). Includes flagged items
    // with chips visible inline.
    const dollarComparable = itemsList.filter(it => it.curr.spend > 0 && it.comp.spend > 0);
    const dollarMoversWithDelta = dollarComparable.map(it => ({
      ...it,
      spendDelta: it.curr.spend - it.comp.spend,
      spendPct: it.comp.spend > 0 ? ((it.curr.spend - it.comp.spend) / it.comp.spend) * 100 : null,
    }));
    const dollarUp = dollarMoversWithDelta
      .filter(it => it.spendDelta > 0)
      .sort((a, b) => b.spendDelta - a.spendDelta)
      .slice(0, 5);
    const dollarDown = dollarMoversWithDelta
      .filter(it => it.spendDelta < 0)
      .sort((a, b) => a.spendDelta - b.spendDelta)
      .slice(0, 5);
    const dollarUpTotal = dollarUp.reduce((s, it) => s + it.spendDelta, 0);
    const dollarDownTotal = dollarDown.reduce((s, it) => s + Math.abs(it.spendDelta), 0);

    // ── New items (current > 0, prior = 0) ─────────────────────────────────
    const newItems = itemsList
      .filter(it => it.currVol > 0 && it.compVol === 0)
      .sort((a, b) => b.curr.spend - a.curr.spend);

    // ── Skipped items (current = 0, prior > 0) ─────────────────────────────
    const skippedItems = itemsList
      .filter(it => it.currVol === 0 && it.compVol > 0)
      .sort((a, b) => b.compVol - a.compVol);

    // ── Weekly breakdown (quarter / year only) ─────────────────────────────
    const weeklyBreakdown = (period === 'quarter' || period === 'year')
      ? bucketByWeek(currLines, range).map(w => ({
          ...w,
          spend: w.lines.reduce((s, l) => s + (Number(l.line_total) || 0), 0),
          lbs: w.lines.reduce((s, l) => s + (Number(l.total_weight) || 0), 0),
          invoices: new Set(w.lines.map(l => l.invoice_id).filter(v => v != null)).size,
        }))
      : null;

    setReport({
      runAt: new Date(),
      period,
      currRange: { ...range },
      compRange: { ...compareRange },
      comparison,
      currTopline,
      compTopline,
      categoryRows,
      vendorRows,
      vendorSpendRows,            // §Spend by vendor
      topItemsBySpend,            // §Top 20 items by spend
      topItemsShareOfTotal,
      dollarUp, dollarDown,       // §Biggest dollar-impact movers
      dollarUpTotal, dollarDownTotal,
      newItems,
      skippedItems,
      weeklyBreakdown,
      itemsList,
    });
    setRunning(false);
  }

  // ── CSV export ──────────────────────────────────────────────────────────
  function exportCsv() {
    if (!report) return;
    const header = ['item_name','category','vendor','period_type','period_label','total_volume_lbs','total_volume_cases','total_spend','num_invoices'];
    const rows = [header.join(',')];
    const currLabel = formatRangeLabel(report.period, report.currRange);
    const compLabel = formatRangeLabel(report.period, report.compRange);

    for (const it of report.itemsList) {
      // One row per (item, period) combination — per spec.
      // Vendor: comma-joined list when multiple. CSV escape handles internal commas.
      const currVendor = it.currVendorList.join(' | ');
      const compVendor = it.compVendorList.join(' | ');
      if (it.curr.spend > 0 || it.curr.lbs > 0 || it.curr.cases > 0) {
        rows.push([
          csvCell(it.name),
          csvCell(it.category),
          csvCell(currVendor),
          'current',
          csvCell(currLabel),
          (it.curr.lbs || 0).toFixed(2),
          (it.curr.cases || 0).toFixed(2),
          (it.curr.spend || 0).toFixed(2),
          it.currInvoiceCount,
        ].join(','));
      }
      if (it.comp.spend > 0 || it.comp.lbs > 0 || it.comp.cases > 0) {
        rows.push([
          csvCell(it.name),
          csvCell(it.category),
          csvCell(compVendor),
          'prior',
          csvCell(compLabel),
          (it.comp.lbs || 0).toFixed(2),
          (it.comp.cases || 0).toFixed(2),
          (it.comp.spend || 0).toFixed(2),
          it.compInvoiceCount,
        ].join(','));
      }
    }

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `posted-inventory-report-${slug(currLabel)}-vs-${slug(compLabel)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-5xl mx-auto" data-invoice-surface>
        {/* Page header */}
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Invoices</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Browse purchases and analyze trends across vendors and items.</p>
        </div>

        {/* ─── Tab bar: Browse | Reports ────────────────────────────────────
            New in this commit. The Reports panel used to sit below a
            scrollable invoice table — operators couldn't find it. Promote
            to a peer tab. Persists per-user so an operator who lives in
            Reports doesn't get reset to Browse on every visit. */}
        <div className="mb-6 no-print">
          <SegmentedPills
            value={activeTab}
            onChange={handleTabChange}
            options={[
              { key: 'browse',  label: 'Browse' },
              { key: 'reports', label: 'Reports' },
            ]}
            size="md"
          />
        </div>

        {activeTab === 'browse' && (
        <>
        {/* ─── Browse tab: Invoice browser ─────────────────────────────────
            The operator's "what did I buy?" scrubbing tool. Filter pills,
            vendor filter, date range, table, "Load more". Same component
            as before — just no longer paired with the Reports panel
            below it. Reports moved to a peer tab. */}
        <InvoiceBrowser
          invoices={browserInvoices}
          lineMeta={browserLineMeta}
          loading={browserLoading}
          hasMore={browserHasMore}
          preset={browserPreset}
          onPresetChange={setBrowserPreset}
          customRange={browserCustomRange}
          onCustomRangeChange={setBrowserCustomRange}
          vendor={browserVendor}
          onVendorChange={setBrowserVendor}
          search={browserSearch}
          onSearchChange={setBrowserSearch}
          distinctVendors={browserDistinctVendors}
          onLoadMore={() => setBrowserPage(p => p + 1)}
          onOpenInvoice={setDrawerInvoice}
          reviewFilter={browserReviewFilter}
          onReviewFilterChange={setBrowserReviewFilter}
          pendingCount={browserPendingCount}
        />
        </>
        )}

        {activeTab === 'reports' && (
        <>
        {/* ─── Reports tab ────────────────────────────────────────────────
            The previously-buried analytics panel, now elevated to a peer
            tab. Four report types: the two original (Period summary +
            Single item history) and the two new (Top spend + Top movers).
            Top spend / Top movers reuse the cost_dashboard_* RPCs that
            power the Cost Dashboard's old TopSpend/TopMovers sections —
            those sections moved here in this commit. */}
        <div className="mb-3 no-print flex items-center gap-3 flex-wrap">
          <span style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
            Report type
          </span>
          <SegmentedPills
            value={reportMode}
            onChange={setReportMode}
            options={[
              { key: 'summary',     label: 'Period summary' },
              { key: 'item',        label: 'Single item history' },
              { key: 'top-spend',   label: 'Top spend' },
              { key: 'top-movers',  label: 'Top movers' },
            ]}
            size="sm"
          />
        </div>

        {reportMode === 'summary' && (
          <>
            {/* Configuration row — hidden on print */}
            <div
              className="rounded-lg p-4 mb-6 no-print"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: period + comparison configuration */}
                <div className="space-y-3 flex-1 min-w-0">
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Reference period
                    </div>
                    <DateStepper
                      period={period}
                      onPeriodChange={setPeriod}
                      range={range}
                      onRangeChange={setRange}
                      alignment="left"
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
                      Compared to
                    </div>
                    <SegmentedPills
                      value={comparison}
                      onChange={setComparison}
                      options={[
                        { key: 'prior',        label: 'Prior period' },
                        { key: 'sameLastYear', label: 'Same period last year' },
                        { key: 'custom',       label: 'Custom range' },
                      ]}
                      size="sm"
                    />
                    {comparison === 'custom' && (
                      <div className="mt-3">
                        <DateStepper
                          period={period}
                          onPeriodChange={() => {}}
                          range={customRange}
                          onRangeChange={setCustomRange}
                          alignment="left"
                        />
                      </div>
                    )}
                    <div className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
                      {`Comparison window: ${formatRangeLabel(period, compareRange)} (${fmtDateShort(compareRange.start)} \u2013 ${fmtDateShort(compareRange.end)})`}
                    </div>
                  </div>
                </div>
                {/* Right: actions */}
                <div className="flex flex-col items-end gap-2">
                  <PrimaryBtn onClick={runReport} disabled={running}>
                    {running ? 'Running\u2026' : 'Run report'}
                  </PrimaryBtn>
                  <div className="flex items-center gap-2">
                    <SecondaryBtn onClick={() => window.print()} disabled={!report} className="!text-xs !px-3 !py-1.5">Print</SecondaryBtn>
                    <SecondaryBtn onClick={exportCsv} disabled={!report} className="!text-xs !px-3 !py-1.5">Export CSV</SecondaryBtn>
                  </div>
                  {staleConfig && (
                    <div className="text-xs" style={{ color: 'var(--text-warning)' }}>
                      {'Config changed \u2014 click Run to refresh'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Output */}
            {!report ? (
              <div
                className="rounded-lg p-12 text-center no-print"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
              >
                <div className="text-3xl mb-3" aria-hidden>{'\uD83D\uDCC4'}</div>
                <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Configure a period and click Run report</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Cross-period totals, vendor concentration, top 20 items by spend, dollar-impact movers, new and skipped items, and (for Quarter / Year) a week-by-week breakdown.
                </div>
              </div>
            ) : (
              <ReportOutput report={report} />
            )}
          </>
        )}

        {reportMode === 'item' && (
          <ItemHistoryReport
            period={itemPeriod}
            onPeriodChange={setItemPeriod}
            range={itemRange}
            onRangeChange={setItemRange}
            allItems={itemAllItems}
            selected={itemSelected}
            onSelect={setItemSelected}
            search={itemSearch}
            onSearchChange={setItemSearch}
            pickerOpen={itemPickerOpen}
            onPickerOpenChange={setItemPickerOpen}
            running={itemHistoryRunning}
            onRun={runItemHistory}
            report={itemHistoryReport}
            chartMetric={itemChartMetric}
            onChartMetricChange={setItemChartMetric}
            onPrint={() => window.print()}
            onExportCsv={exportItemHistoryCsv}
          />
        )}

        {(reportMode === 'top-spend' || reportMode === 'top-movers') && (
          <TopReports
            reportType={reportMode}
            companyId={companyId}
            onOpenDrillIn={openDrillInForRow}
          />
        )}
        </>
        )}
      </div>

      {/* Invoice detail drawer (slide-over) */}
      <InvoiceDetailDrawer
        open={drawerInvoice != null}
        onClose={() => setDrawerInvoice(null)}
        companyId={companyId}
        invoice={drawerInvoice}
        onParserToast={setParserToast}
      />

      {/* Parser feedback toast — uses systemKit.Toast (3s auto-dismiss). */}
      <Toast toast={parserToast} onDismiss={() => setParserToast(null)} />

      {/* Item drill-in slide-over — opened from Top spend / Top movers report rows.
          Reuses the canonical drill-in component from the Invoice Review modal. */}
      <InvoiceReviewItemDrillIn
        open={drillIn != null}
        onClose={() => setDrillIn(null)}
        companyId={companyId}
        foodItemId={drillIn?.foodItemId}
        itemName={drillIn?.itemName}
        itemCategory={null}
        isCatchWeight={drillIn?.isCatchWeight}
        currentInvoiceId={drillIn?.currentInvoiceId}
        currentInvoiceDate={drillIn?.currentInvoiceDate}
        parseDiff={drillIn?.parseDiff}
      />
    </div>
  );
}

// ── Report output sections ─────────────────────────────────────────────────

function ReportOutput({ report }) {
  const {
    period, currRange, compRange, comparison, runAt,
    currTopline, compTopline,
    categoryRows, vendorRows,
    vendorSpendRows, topItemsBySpend, topItemsShareOfTotal,
    dollarUp, dollarDown, dollarUpTotal, dollarDownTotal,
    newItems, skippedItems, weeklyBreakdown,
  } = report;

  const currLabel = formatRangeLabel(period, currRange);
  const compLabel = formatRangeLabel(period, compRange);
  const comparisonNoun = comparison === 'sameLastYear' ? 'Same period last year'
                       : comparison === 'custom' ? 'Custom range'
                       : 'Prior period';
  const periodNoun = period === 'week' ? 'week' : period === 'month' ? 'month' : period === 'quarter' ? 'quarter' : 'year';

  // Top-line deltas
  const spendPct = deltaPct(currTopline.spend, compTopline.spend);
  const lbsPct = deltaPct(currTopline.lbs, compTopline.lbs);
  const itemsPct = deltaPct(currTopline.items, compTopline.items);
  const invoicesPct = deltaPct(currTopline.invoices, compTopline.invoices);

  return (
    <div className="space-y-6">
      {/* ── Title block ─────────────────────────────────────────────────── */}
      <div data-invoice-section>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          {`${currLabel} Inventory Report`}
        </h2>
        <div className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
          {`Compared to ${compLabel} (${comparisonNoun}) \u00B7 ${fmtDateShort(currRange.start)} \u2013 ${fmtDateShort(currRange.end)} vs ${fmtDateShort(compRange.start)} \u2013 ${fmtDateShort(compRange.end)}`}
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          {`Generated ${runAt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
        </div>
      </div>

      {/* ── Top-line tiles ──────────────────────────────────────────────── */}
      <div data-invoice-section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Total spend"
          value={fmtMoney(currTopline.spend)}
          secondary={spendPct != null ? `${fmtDelta(spendPct)} vs ${comparisonNoun.toLowerCase()}` : `vs ${comparisonNoun.toLowerCase()}`}
          valueTone={deltaToneSpend(spendPct) || undefined}
        />
        <KpiCard
          label="Total weight"
          value={`${fmtNum(Math.round(currTopline.lbs))} lbs`}
          secondary={lbsPct != null ? fmtDelta(lbsPct) : `vs ${comparisonNoun.toLowerCase()}`}
          valueTone={deltaTone(lbsPct) || undefined}
        />
        <KpiCard
          label="Distinct items"
          value={fmtNum(currTopline.items)}
          secondary={itemsPct != null ? fmtDelta(itemsPct) : `vs ${comparisonNoun.toLowerCase()}`}
          valueTone={deltaTone(itemsPct) || undefined}
        />
        <KpiCard
          label="Total invoices"
          value={fmtNum(currTopline.invoices)}
          secondary={invoicesPct != null ? fmtDelta(invoicesPct) : `vs ${comparisonNoun.toLowerCase()}`}
        />
      </div>

      {/* ── §Spend by vendor (NEW) ──────────────────────────────────────────
          Vendor concentration — owner's first business question. Distinct
          from §By vendor breakdown below (which is the volume + spend
          comparison table). This section is the "where my money went"
          ranked summary. */}
      <ReportSection title="Spend by vendor">
        {vendorSpendRows.length === 0 ? (
          <EmptySection text="No vendor data in this period." />
        ) : (
          <ComparisonTable
            headerCols={['Vendor', 'Invoices', 'Total spend', '% of period', 'vs prior']}
            rows={vendorSpendRows.map(v => ({
              key: v.vendor,
              cells: [
                { value: fmtVendor(v.vendor), align: 'left' },
                { value: fmtNum(v.invoiceCount), align: 'right' },
                { value: fmtMoney(v.currSpend), align: 'right' },
                { value: `${v.shareOfPeriod.toFixed(0)}%`, align: 'right', muted: true },
                v.kind === 'new'
                  ? { value: 'new', align: 'right', tone: 'success' }
                  : v.kind === 'dropped'
                  ? { value: 'dropped', align: 'right', muted: true }
                  : { value: fmtDelta(v.changePct), align: 'right', tone: deltaToneSpend(v.changePct) },
              ],
            }))}
          />
        )}
      </ReportSection>

      {/* ── By category ─────────────────────────────────────────────────── */}
      <ReportSection title="By category">
        {categoryRows.length === 0 ? (
          <EmptySection text="No purchases in this period." />
        ) : (
          <ComparisonTable
            headerCols={['Category', 'Volume this', 'Volume prior', `\u0394 vol`, 'Spend this', 'Spend prior', `\u0394 spend`]}
            rows={categoryRows.map(c => {
              const vPct = deltaPct(c.currLbs, c.compLbs);
              const sPct = deltaPct(c.currSpend, c.compSpend);
              return {
                key: c.category,
                cells: [
                  { value: c.category, align: 'left' },
                  { value: `${fmtNum(Math.round(c.currLbs))} lbs`, align: 'right' },
                  { value: c.compLbs > 0 ? `${fmtNum(Math.round(c.compLbs))} lbs` : '\u2014', align: 'right', muted: true },
                  { value: fmtDelta(vPct), align: 'right', tone: deltaTone(vPct) },
                  { value: fmtMoney(c.currSpend), align: 'right' },
                  { value: c.compSpend > 0 ? fmtMoney(c.compSpend) : '\u2014', align: 'right', muted: true },
                  { value: fmtDelta(sPct), align: 'right', tone: deltaToneSpend(sPct) },
                ],
              };
            })}
          />
        )}
      </ReportSection>

      {/* ── By vendor ───────────────────────────────────────────────────── */}
      <ReportSection title="By vendor">
        {vendorRows.length === 0 ? (
          <EmptySection text="No vendor data in this period." />
        ) : (
          <ComparisonTable
            headerCols={['Vendor', 'Volume this', 'Volume prior', `\u0394 vol`, 'Spend this', 'Spend prior', `\u0394 spend`]}
            rows={vendorRows.map(v => {
              const vPct = deltaPct(v.currLbs, v.compLbs);
              const sPct = deltaPct(v.currSpend, v.compSpend);
              return {
                key: v.vendor,
                cells: [
                  { value: fmtVendor(v.vendor), align: 'left' },
                  { value: `${fmtNum(Math.round(v.currLbs))} lbs`, align: 'right' },
                  { value: v.compLbs > 0 ? `${fmtNum(Math.round(v.compLbs))} lbs` : '\u2014', align: 'right', muted: true },
                  { value: fmtDelta(vPct), align: 'right', tone: deltaTone(vPct) },
                  { value: fmtMoney(v.currSpend), align: 'right' },
                  { value: v.compSpend > 0 ? fmtMoney(v.compSpend) : '\u2014', align: 'right', muted: true },
                  { value: fmtDelta(sPct), align: 'right', tone: deltaToneSpend(sPct) },
                ],
              };
            })}
          />
        )}
      </ReportSection>

      {/* ── §Top 20 items by spend (NEW) ────────────────────────────────────
          The 80/20 sweet spot for "where my food dollars went." Clickable
          rows open the item in Inventory Trends (deep-dive surface). */}
      <ReportSection title="Top 20 items by spend">
        {topItemsBySpend.length === 0 ? (
          <EmptySection text="No item spend in this period." />
        ) : (
          <>
            <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
              <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-subtle)' }}>
                    {['#', 'Item', 'Category', '$ this period', '$ prior period', '\u0394'].map((h, i) => (
                      <th key={h + i}
                        className="px-3 py-2"
                        style={{
                          fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                          color: 'var(--text-secondary)',
                          borderBottom: '1px solid var(--border)',
                          textAlign: i >= 3 ? 'right' : 'left',
                        }}
                      >{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topItemsBySpend.map((it, ri) => {
                    const sPct = deltaPct(it.curr.spend, it.comp.spend);
                    return (
                      <tr key={it.id}
                        onClick={() => navigate('inventory', 'trends')}
                        style={{
                          borderBottom: ri < topItemsBySpend.length - 1 ? '1px solid var(--border)' : 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', width: 36 }}>{ri + 1}</td>
                        <td className="px-3 py-2 text-sm">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{it.name}</span>
                            {it.needs_review && (
                              <StatusPill tone="amber">Needs review</StatusPill>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>{it.category}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{fmtMoney(it.curr.spend)}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{it.comp.spend > 0 ? fmtMoney(it.comp.spend) : '\u2014'}</td>
                        <td className="px-3 py-2 text-sm"
                          style={{
                            color: sPct == null ? 'var(--text-muted)' : (sPct >= 0 ? 'var(--text-critical)' : 'var(--text-success)'),
                            fontVariantNumeric: 'tabular-nums',
                            textAlign: 'right',
                          }}
                        >{sPct == null ? '\u2014' : fmtDelta(sPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              {`These ${topItemsBySpend.length} items represent ${topItemsShareOfTotal.toFixed(0)}% of your total food spend this period.`}
            </div>
          </>
        )}
      </ReportSection>

      {/* ── §Biggest dollar-impact movers (REPLACES %-based version) ────────
          Owners think in dollars. The %-based view is already the right
          answer on Inventory Trends (where operators want movement signals
          normalized for size). See DESIGN_SYSTEM.md §Dollar-impact vs
          percentage movers. Includes flagged items with chips inline. */}
      <ReportSection title="Biggest dollar-impact movers">
        <div className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
          Items with comparable activity in both periods, ranked by absolute dollar change. Items new or skipped this period appear in their own sections below.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DollarMoverColumn
            heading={`Cost up the most ($) this ${periodNoun}`}
            tone="critical"
            rows={dollarUp}
            totalDelta={dollarUpTotal}
            captionDirection="added"
            emptyText="No items rose vs comparison."
          />
          <DollarMoverColumn
            heading={`Cost down the most ($) this ${periodNoun}`}
            tone="success"
            rows={dollarDown}
            totalDelta={dollarDownTotal}
            captionDirection="saved"
            emptyText="No items fell vs comparison."
          />
        </div>
      </ReportSection>

      {/* ── New this period ─────────────────────────────────────────────── */}
      <ReportSection title="New this period">
        {newItems.length === 0 ? (
          <EmptySection text={`No items new this ${periodNoun}.`} />
        ) : (
          <CompactItemList
            rows={newItems.map(it => ({
              key: it.id,
              name: it.name,
              category: it.category,
              metaLeft: it.useWeight ? `${fmtNum(Math.round(it.currVol))} lbs` : `${fmtNum(it.currVol)} cases`,
              metaRight: fmtMoney(it.curr.spend),
            }))}
          />
        )}
      </ReportSection>

      {/* ── Skipped this period ─────────────────────────────────────────── */}
      <ReportSection title="Skipped this period">
        {skippedItems.length === 0 ? (
          <EmptySection text={`No items skipped this ${periodNoun}.`} />
        ) : (
          <CompactItemList
            rows={skippedItems.map(it => ({
              key: it.id,
              name: it.name,
              category: it.category,
              metaLeft: it.useWeight ? `${fmtNum(Math.round(it.compVol))} lbs prior` : `${fmtNum(it.compVol)} cases prior`,
              metaRight: it.comp.lastDate ? `Last: ${fmtDateShort(it.comp.lastDate)}` : '',
            }))}
          />
        )}
      </ReportSection>

      {/* ── Weekly breakdown (quarter/year only) ────────────────────────── */}
      {weeklyBreakdown && (
        <ReportSection title={`Week-by-week breakdown \u2014 ${currLabel}`}>
          <ComparisonTable
            headerCols={['Week', 'Spend', 'Lbs', 'Invoices']}
            rows={weeklyBreakdown.map(w => ({
              key: `${w.weekStart}`,
              cells: [
                { value: `${fmtDateShort(w.weekStart)} \u2013 ${fmtDateShort(w.weekEnd)}`, align: 'left' },
                { value: w.spend > 0 ? fmtMoneyExact(w.spend) : '\u2014', align: 'right' },
                { value: w.lbs > 0 ? fmtNum(Math.round(w.lbs)) : '\u2014', align: 'right' },
                { value: w.invoices > 0 ? fmtNum(w.invoices) : '\u2014', align: 'right' },
              ],
            }))}
            footer={(() => {
              const totalSpend = weeklyBreakdown.reduce((s, w) => s + w.spend, 0);
              const totalLbs = weeklyBreakdown.reduce((s, w) => s + w.lbs, 0);
              const totalInv = weeklyBreakdown.reduce((s, w) => s + w.invoices, 0);
              return [
                { value: 'Total', align: 'left', strong: true },
                { value: fmtMoneyExact(totalSpend), align: 'right', strong: true },
                { value: fmtNum(Math.round(totalLbs)), align: 'right', strong: true },
                { value: fmtNum(totalInv), align: 'right', strong: true },
              ];
            })()}
          />
        </ReportSection>
      )}

      {/* Print footer — generation provenance, repeats per page in browsers
          that respect running headers/footers. Otherwise just appears once. */}
      <div className="print-footer text-xs italic mt-6" style={{ color: 'var(--text-muted)' }}>
        {`Posted \u00B7 Generated ${runAt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function ReportSection({ title, children }) {
  return (
    <div data-invoice-section>
      <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function EmptySection({ text }) {
  return (
    <div className="rounded-lg p-6 text-center text-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
      {text}
    </div>
  );
}

function ComparisonTable({ headerCols, rows, footer }) {
  // Use real <table> markup so browser print headers can repeat per page.
  return (
    <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <table className="w-full" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bg-subtle)' }}>
            {headerCols.map((h, i) => (
              <th key={h + i}
                className="px-3 py-2 text-left"
                style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                  color: 'var(--text-secondary)',
                  borderBottom: '1px solid var(--border)',
                  textAlign: i === 0 ? 'left' : 'right',
                }}
              >{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={r.key}
              style={{
                borderBottom: ri < rows.length - 1 ? '1px solid var(--border)' : 'none',
              }}
            >
              {r.cells.map((c, ci) => (
                <td key={ci}
                  className="px-3 py-2 text-sm"
                  style={{
                    color: c.tone === 'success' ? 'var(--text-success)'
                         : c.tone === 'critical' ? 'var(--text-critical)'
                         : c.muted ? 'var(--text-muted)'
                         : 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: c.align || 'left',
                    fontWeight: c.strong ? 600 : 400,
                  }}
                >{c.value}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr style={{ background: 'var(--bg-subtle)', borderTop: '1px solid var(--border)' }}>
              {footer.map((c, ci) => (
                <td key={ci}
                  className="px-3 py-2 text-sm"
                  style={{
                    color: 'var(--text-primary)',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: c.align || 'left',
                    fontWeight: c.strong ? 600 : 500,
                  }}
                >{c.value}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// DollarMoverColumn — replaces the prior %-based MovementColumn. Each row
// shows the dollar-spend delta for an item, with the % delta as secondary
// text. Caption sentence below the column ("Together these 5 items added
// $X to your food spend vs prior period").
function DollarMoverColumn({ heading, rows, tone, totalDelta, captionDirection, emptyText }) {
  const headingColor = tone === 'success' ? 'var(--text-success)' : tone === 'critical' ? 'var(--text-critical)' : 'var(--text-secondary)';
  return (
    <div>
      <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div className="px-4 py-2.5" style={{
          background: 'var(--bg-subtle)',
          borderBottom: '1px solid var(--border)',
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
          color: headingColor,
        }}>{heading}</div>
        {rows.length === 0 ? (
          <div className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>{emptyText}</div>
        ) : (
          rows.map((it, i) => {
            const direction = it.spendDelta >= 0 ? '+' : '\u2212';
            return (
              <div key={it.id}
                className="grid items-center"
                style={{
                  gridTemplateColumns: '1fr auto',
                  padding: '10px 16px',
                  borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                  gap: 12,
                }}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate inline-flex items-center gap-1.5 flex-wrap" style={{ color: 'var(--text-primary)' }}>
                    <span>{it.name}</span>
                    {it.needs_review && (
                      <span style={{ background: 'var(--bg-warning-subtle)', color: 'var(--text-warning)', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 9999 }}>Needs review</span>
                    )}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    {`${it.category} \u00B7 ${fmtMoney(it.comp.spend)} \u2192 ${fmtMoney(it.curr.spend)}`}
                  </div>
                </div>
                <div className="text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <div className="text-sm font-semibold" style={{ color: headingColor }}>
                    {`${direction}${fmtMoney(Math.abs(it.spendDelta))}`}
                  </div>
                  {it.spendPct != null && (
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {fmtDelta(it.spendPct)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      {rows.length > 0 && totalDelta > 0 && (
        <div className="text-xs mt-2" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {`Together these ${rows.length} item${rows.length === 1 ? '' : 's'} ${captionDirection} `}
          <strong style={{ color: headingColor }}>{fmtMoney(totalDelta)}</strong>
          {` to your food spend vs prior period.`}
        </div>
      )}
    </div>
  );
}

function CompactItemList({ rows }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      {rows.map((r, i) => (
        <div key={r.key}
          className="grid items-center"
          style={{
            gridTemplateColumns: '1fr auto auto',
            padding: '10px 16px',
            borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
            gap: 12,
          }}
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{r.name}</div>
            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.category}</div>
          </div>
          <div className="text-sm" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
            {r.metaLeft}
          </div>
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {r.metaRight}
          </div>
        </div>
      ))}
    </div>
  );
}


// ─── ViaEmailChip ───────────────────────────────────────────────────────────
//
// Tiny "via email" provenance pill on Invoices browser rows whose source_type
// is 'email_inbound' (i.e. ingested via parse@invoices.tryposted.app forward,
// not manual Upload Invoice). Purely informational — operator hover sees the
// title attribute. See DESIGN_SYSTEM.md §Email-inbound source.
function ViaEmailChip() {
  return (
    <span
      title="Forwarded via email to parse@invoices.tryposted.app"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        padding: '1px 7px',
        fontSize: 10, fontWeight: 600,
        background: 'var(--bg-subtle)',
        color: 'var(--text-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
        <path d="M4 4h16v16H4z" />
        <path d="M4 6l8 7 8-7" />
      </svg>
      via email
    </span>
  );
}

// ─── ParseSummaryChip ───────────────────────────────────────────────────────
//
// Inline chip rendered on each browser row when the invoice has a
// parse_summary blob (everything uploaded post-2026-04-27). Clickable —
// opens the Invoice Review modal via openInvoiceReview() so the
// operator can drill in without leaving the list. Click stops
// propagation so the row's underlying drawer-open click doesn't fire.
//
// Tone:
//   review_status='pending' AND moved_count > 0 → tone driven by the
//     summary's flags (red if has_red_flag, amber if pack_changed or
//     any movement, neutral if all flat).
//   review_status='pending' AND moved_count === 0 → no chip (suppressed
//     noise — pure-flat invoices don't need an "items moved" badge).
//   review_status='acknowledged' or 'dismissed' → muted "Reviewed" chip
//     so the operator sees they've already engaged.
//   parse_summary IS NULL (pre-tracking invoices) → renders nothing.
//
// `getMovedCount` is defensive against partial summary blobs (e.g.,
// hand-rolled SQL UPDATE during testing): falls back to summing the
// constituent class counts if `moved_count` is missing.
function getMovedCount(summary) {
  if (!summary) return 0;
  if (typeof summary.moved_count === 'number') return summary.moved_count;
  return (Number(summary.up) || 0)
    + (Number(summary.down) || 0)
    + (Number(summary.pack_changed) || 0)
    + (Number(summary.new_item) || 0);
}

function ParseSummaryChip({ invoice }) {
  const summary = invoice?.parse_summary;
  const status = invoice?.review_status;
  if (!summary && (!status || status === 'pending')) return null;

  const moved = getMovedCount(summary);

  // Reviewed marker for acknowledged/dismissed invoices.
  if (status === 'acknowledged' || status === 'dismissed') {
    if (!summary || moved === 0) return null;
    return (
      <span
        title={`Reviewed · ${moved} item${moved === 1 ? '' : 's'} moved`}
        style={{
          display: 'inline-block', padding: '2px 6px',
          fontSize: 10, fontWeight: 600,
          background: 'var(--bg-subtle)',
          color: 'var(--text-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Reviewed
      </span>
    );
  }

  if (!summary) return null;
  if (moved === 0) return null;

  const tone = summary.has_red_flag ? 'red'
    : (summary.pack_changed > 0 || summary.up > 0 || summary.down > 0) ? 'amber'
    : 'neutral';
  const colors =
    tone === 'red' ? {
      bg: 'color-mix(in srgb, var(--text-critical) 12%, transparent)',
      fg: 'var(--text-critical)',
      border: 'color-mix(in srgb, var(--text-critical) 35%, transparent)',
    } : tone === 'amber' ? {
      bg: 'color-mix(in srgb, var(--text-warning) 14%, transparent)',
      fg: 'var(--text-warning)',
      border: 'color-mix(in srgb, var(--text-warning) 40%, transparent)',
    } : {
      bg: 'var(--bg-subtle)',
      fg: 'var(--text-secondary)',
      border: 'var(--border)',
    };

  const label = `${moved} item${moved === 1 ? '' : 's'} moved`;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('app:open-invoice-review', { detail: { invoiceId: invoice.id } }));
        }
      }}
      title="Review parse"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px',
        fontSize: 10, fontWeight: 700,
        background: colors.bg, color: colors.fg,
        border: `1px solid ${colors.border}`,
        borderRadius: 9999,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      {summary.has_red_flag && <span style={{ fontSize: 9 }}>⚑</span>}
      {label}
    </button>
  );
}

// ─── InvoiceBrowser ─────────────────────────────────────────────────────────
// Top-of-page hero zone. Filter row + invoice list + Load more button.
// Each row clickable → opens InvoiceDetailDrawer.
//
// Filters apply identically to the list query — debounced search, vendor
// dropdown, date-range preset chips. Pagination is explicit "Load more"
// (per spec: operators benefit from position awareness more than from
// endless feed).
function InvoiceBrowser({
  invoices, lineMeta, loading, hasMore,
  preset, onPresetChange, customRange, onCustomRangeChange,
  vendor, onVendorChange,
  search, onSearchChange,
  distinctVendors,
  onLoadMore, onOpenInvoice,
  reviewFilter = 'all',
  onReviewFilterChange,
  pendingCount = 0,
}) {
  const showCustom = preset === 'custom';
  return (
    <div
      className="rounded-lg overflow-hidden mb-6 no-print"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
    >
      {/* Review-status filter pills (4 pills: All / Unreviewed / Acknowledged
          / Dismissed). Renders above the rest of the filter controls because
          this is the operator's primary triage axis since the live-parse
          notification shipped — "show me what I haven't looked at yet". */}
      {onReviewFilterChange && (
        <div className="px-4 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
          <SegmentedPills
            value={reviewFilter}
            onChange={onReviewFilterChange}
            options={[
              { key: 'all', label: 'All' },
              { key: 'unreviewed', label: 'Unreviewed', count: pendingCount > 0 ? pendingCount : undefined },
              { key: 'acknowledged', label: 'Acknowledged' },
              { key: 'dismissed', label: 'Dismissed' },
            ]}
            size="sm"
          />
        </div>
      )}

      {/* Filter row */}
      <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative" style={{ width: 240 }}>
            <input
              type="text"
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Search invoice # or vendor"
              className="w-full rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            {search && (
              <button
                onClick={() => onSearchChange('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: 14, padding: 4, lineHeight: 1,
                }}
              >{'\u2715'}</button>
            )}
          </div>

          {/* Vendor dropdown */}
          <select
            value={vendor}
            onChange={e => onVendorChange(e.target.value)}
            className="rounded-lg px-3 py-1.5 text-sm focus:outline-none"
            style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
          >
            <option value="">All vendors</option>
            {distinctVendors.map(v => (
              <option key={v} value={v}>{v.replace(/\n/g, ' \u00B7 ')}</option>
            ))}
          </select>

          {/* Date-range preset chips */}
          <SegmentedPills
            value={preset}
            onChange={onPresetChange}
            options={[
              ...BROWSER_RANGE_PRESETS.map(p => ({ key: p.key, label: p.label })),
              { key: 'custom', label: 'Custom' },
            ]}
            size="sm"
          />

          {/* Result count (right-aligned) */}
          <div className="ml-auto text-xs" style={{ color: 'var(--text-muted)' }}>
            {loading && invoices.length === 0
              ? 'Loading\u2026'
              : `Showing ${invoices.length}${hasMore ? '+' : ''} invoice${invoices.length === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Custom date range — second line when active */}
        {showCustom && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>From</span>
            <input
              type="date" value={customRange.start || ''}
              onChange={e => onCustomRangeChange({ ...customRange, start: e.target.value })}
              className="rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
            />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>to</span>
            <input
              type="date" value={customRange.end || ''}
              onChange={e => onCustomRangeChange({ ...customRange, end: e.target.value })}
              className="rounded-lg px-3 py-1.5 text-sm focus:outline-none"
              style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
            />
          </div>
        )}
      </div>

      {/* List */}
      <div className="grid text-xs" style={{
        gridTemplateColumns: '90px 1.4fr 110px 60px 90px 24px',
        background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)',
      }}>
        {[
          { label: 'Date',     align: 'left' },
          { label: 'Vendor',   align: 'left' },
          { label: 'Invoice #', align: 'left' },
          { label: 'Lines',    align: 'right' },
          { label: 'Total',    align: 'right' },
          { label: '',         align: 'left' },
        ].map(h => (
          <div key={h.label} className="px-3 py-2"
            style={{
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
              color: 'var(--text-secondary)',
              textAlign: h.align,
            }}
          >{h.label}</div>
        ))}
      </div>

      {invoices.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          {loading ? 'Loading\u2026' : 'No invoices match these filters.'}
        </div>
      ) : (
        invoices.map((inv, i) => {
          const meta = lineMeta[inv.id] || { count: null, total: null };
          // Row click routing:
          //   pending + has summary movement → open InvoiceReviewModal so
          //     the operator drops straight into the diff.
          //   anything else → legacy detail drawer (file image preview).
          //   This keeps muscle memory for historical / acknowledged rows
          //   while making unreviewed rows do the high-value action.
          const summaryMoved = getMovedCount(inv.parse_summary);
          const handleRowClick = () => {
            if (inv.review_status === 'pending' && summaryMoved > 0) {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('app:open-invoice-review', { detail: { invoiceId: inv.id } }));
              }
              return;
            }
            onOpenInvoice(inv);
          };
          return (
            <div
              key={inv.id}
              onClick={handleRowClick}
              className="grid text-sm cursor-pointer transition-colors"
              style={{
                gridTemplateColumns: '90px 1.4fr 110px 60px 90px 24px',
                borderBottom: i < invoices.length - 1 ? '1px solid var(--border)' : 'none',
                minHeight: 44,
                alignItems: 'center',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                {fmtDateShort(inv.invoice_date)}
              </div>
              <div className="px-3 py-2" style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }} title={inv.vendor_name}>
                <span className="truncate">{fmtVendor(inv.vendor_name)}</span>
                {inv.source_type === 'email_inbound' && <ViaEmailChip />}
                <ParseSummaryChip invoice={inv} />
              </div>
              <div className="px-3 py-2 truncate" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
                {inv.invoice_number || '\u2014'}
              </div>
              <div className="px-3 py-2" style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                {meta.count != null ? meta.count : '\u2014'}
              </div>
              <div className="px-3 py-2" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, textAlign: 'right' }}>
                {inv.total_amount != null ? fmtMoney(inv.total_amount) : '\u2014'}
              </div>
              <div className="px-3 py-2" style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
                {'\u2192'}
              </div>
            </div>
          );
        })
      )}

      {/* Load more */}
      {hasMore && (
        <div className="px-4 py-3 text-center" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
          <SecondaryBtn onClick={onLoadMore} disabled={loading} className="!text-xs !px-4 !py-1.5">
            {loading ? 'Loading\u2026' : 'Load more'}
          </SecondaryBtn>
        </div>
      )}
    </div>
  );
}

// ─── ItemHistoryReport ──────────────────────────────────────────────────────
// Per-item focused report. Configuration row (item picker + date range +
// Run/Print/Export CSV) on top, full-width chart with metric toggle
// (Volume / $/lb / $/case), summary stats, every-purchase table.
//
// Print stylesheet in src/index.css covers this mode via the same
// [data-invoice-surface] / [data-invoice-section] selectors. Sidebar +
// configuration row hide on print; chart + stats + table flow naturally
// across pages with thead repeating.
//
// CSV export here exports RAW invoice lines (one row per invoice line)
// since the focus is already a single item — aggregation isn't useful.
// Filename slug rule documented at slugifyItemName.
function ItemHistoryReport({
  period, onPeriodChange,
  range, onRangeChange,
  allItems, selected, onSelect,
  search, onSearchChange,
  pickerOpen, onPickerOpenChange,
  running, onRun, report,
  chartMetric, onChartMetricChange,
  onPrint, onExportCsv,
}) {
  // Filtered picker results (min 2 chars to filter; otherwise show all,
  // capped to 200 for performance).
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return allItems.slice(0, 200);
    return allItems.filter(it => (it.normalized_name || '').toLowerCase().includes(q)).slice(0, 200);
  }, [allItems, search]);

  return (
    <>
      {/* Configuration row */}
      <div
        className="rounded-lg p-4 mb-6 no-print"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-3 flex-1 min-w-0">
            {/* Item picker */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
                Item
              </div>
              <div className="relative" style={{ maxWidth: 480 }}>
                <input
                  type="text"
                  value={pickerOpen ? search : (selected ? selected.normalized_name : '')}
                  onChange={e => { onSearchChange(e.target.value); onPickerOpenChange(true); }}
                  onFocus={e => { onSearchChange(''); onPickerOpenChange(true); e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(109, 40, 217, 0.1)'; }}
                  onBlur={e => { setTimeout(() => onPickerOpenChange(false), 200); e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                  placeholder={selected ? '' : 'Search items (min 2 characters)'}
                  className="w-full rounded-lg px-3 py-1.5 text-sm focus:outline-none"
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)' }}
                />
                {pickerOpen && filteredItems.length > 0 && (
                  <div
                    className="absolute z-10 mt-1 w-full rounded-lg overflow-y-auto"
                    style={{
                      maxHeight: 280,
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  >
                    {filteredItems.map(it => (
                      <div
                        key={it.id}
                        onMouseDown={() => { onSelect(it); onPickerOpenChange(false); onSearchChange(''); }}
                        className="px-3 py-2 cursor-pointer text-sm flex items-center gap-2 flex-wrap"
                        style={{
                          color: 'var(--text-primary)',
                          background: selected?.id === it.id ? 'var(--bg-subtle)' : 'transparent',
                        }}
                        onMouseEnter={e => { if (selected?.id !== it.id) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                        onMouseLeave={e => { if (selected?.id !== it.id) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ fontWeight: 500 }}>{it.normalized_name}</span>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{it.category}</span>
                        {it.is_catch_weight && <StatusPill tone="neutral">catch-weight</StatusPill>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Range — period-aware DateStepper, defaults to last 12 months */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 6 }}>
                Range
              </div>
              <DateStepper
                period={period}
                onPeriodChange={onPeriodChange}
                range={range}
                onRangeChange={onRangeChange}
                alignment="left"
              />
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <PrimaryBtn onClick={onRun} disabled={!selected || running}>
              {running ? 'Running\u2026' : 'Run report'}
            </PrimaryBtn>
            <div className="flex items-center gap-2">
              <SecondaryBtn onClick={onPrint} disabled={!report} className="!text-xs !px-3 !py-1.5">Print</SecondaryBtn>
              <SecondaryBtn onClick={onExportCsv} disabled={!report} className="!text-xs !px-3 !py-1.5">Export CSV</SecondaryBtn>
            </div>
          </div>
        </div>
      </div>

      {/* Output */}
      {!report ? (
        <div
          className="rounded-lg p-12 text-center no-print"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
        >
          <div className="text-3xl mb-3" aria-hidden>{'\uD83D\uDCC8'}</div>
          <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Pick an item and click Run report</div>
          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Get a focused price-and-purchase history for one SKU \u2014 chart, summary stats, every invoice. Useful for vendor meetings.
          </div>
        </div>
      ) : (
        <ItemHistoryOutput
          report={report}
          chartMetric={chartMetric}
          onChartMetricChange={onChartMetricChange}
        />
      )}
    </>
  );
}

function ItemHistoryOutput({ report, chartMetric, onChartMetricChange }) {
  const { item, range: r, lines, runAt, stats } = report;
  const { useWeight, invoiceCount, totalLbs, totalCases, totalSpend, avgPerLb, minPerLb, maxPerLb, topVendor } = stats;

  // Chart series — invoice_date sorted ascending so the line reads
  // left-to-right chronologically.
  const sortedLines = [...lines].sort((a, b) => String(a.invoice_date).localeCompare(String(b.invoice_date)));
  const chartSeries = sortedLines.map(l => {
    let value = null;
    if (chartMetric === 'perLb') value = l.price_per_lb != null ? Number(l.price_per_lb) : null;
    else if (chartMetric === 'perCase') value = l.unit_price != null ? Number(l.unit_price) : null;
    else if (chartMetric === 'volume') value = useWeight ? Number(l.total_weight) || 0 : Number(l.quantity) || 0;
    return { date: l.invoice_date, value };
  }).filter(p => p.value != null && isFinite(p.value));

  const yFormat = (v) => {
    if (chartMetric === 'perLb') return `$${v.toFixed(2)}`;
    if (chartMetric === 'perCase') return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
    return useWeight ? `${Math.round(v)} lb` : `${v.toFixed(1)} cs`;
  };

  // Chart "x range" — span the actual data instead of using a fixed
  // calendar window, since item history can be sparse.
  const chartRange = {
    start: sortedLines[0]?.invoice_date || r.start,
    end: sortedLines[sortedLines.length - 1]?.invoice_date || r.end,
  };

  return (
    <div className="space-y-6">
      {/* ── Title block ─────────────────────────────────────────────────── */}
      <div data-invoice-section>
        <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
          {item.normalized_name}
        </h2>
        <div className="text-sm mt-1 inline-flex items-center gap-2 flex-wrap" style={{ color: 'var(--text-secondary)' }}>
          <span>{item.category || 'Uncategorized'}</span>
          {item.is_catch_weight && <StatusPill tone="neutral">catch-weight</StatusPill>}
          {topVendor && <span>{`\u00B7 primarily ${fmtVendor(topVendor.name)} (${topVendor.count} of ${topVendor.total} invoices)`}</span>}
        </div>
        <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          {`${fmtDateShort(r.start)} \u2013 ${fmtDateShort(r.end)} \u00B7 Generated ${runAt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
        </div>
      </div>

      {/* ── Chart ───────────────────────────────────────────────────────── */}
      <div data-invoice-section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' }}>
            {chartMetric === 'perLb' ? 'Price per pound over time' : chartMetric === 'perCase' ? 'Price per case over time' : 'Volume over time'}
          </div>
          <SegmentedPills
            value={chartMetric}
            onChange={onChartMetricChange}
            options={[
              { key: 'perLb',   label: '$/lb' },
              { key: 'perCase', label: '$/case' },
              { key: 'volume',  label: useWeight ? 'Volume (lbs)' : 'Volume (cases)' },
            ]}
            size="sm"
          />
        </div>
        {chartSeries.length === 0 ? (
          <EmptySection text={`No ${chartMetric === 'perLb' ? '$/lb' : chartMetric === 'perCase' ? '$/case' : 'volume'} data in this range.`} />
        ) : (
          <PremiumLineChart
            series={chartSeries}
            range={chartRange}
            priorRange={chartRange}
            yFormat={yFormat}
            gradientId="item-history-chart-fill"
          />
        )}
      </div>

      {/* ── Summary stats ───────────────────────────────────────────────── */}
      <div data-invoice-section>
        <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
          Summary
        </div>
        <div className="rounded-lg overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <tbody>
              <SummaryRow label="Invoices in range" value={fmtNum(invoiceCount)} />
              <SummaryRow label="Total purchased" value={useWeight ? `${fmtNum(Math.round(totalLbs))} lbs` : `${fmtNum(totalCases)} cases`} />
              <SummaryRow label="Total spent" value={fmtMoneyExact(totalSpend)} />
              {avgPerLb != null && <SummaryRow label="Avg $/lb" value={`$${avgPerLb.toFixed(2)}`} />}
              {minPerLb != null && maxPerLb != null && (
                <SummaryRow label="Range $/lb" value={`$${minPerLb.toFixed(2)} \u2013 $${maxPerLb.toFixed(2)}`} />
              )}
              {topVendor && <SummaryRow label="Most-used vendor" value={`${fmtVendor(topVendor.name)} (${topVendor.count} of ${topVendor.total} invoices)`} last />}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Every purchase ──────────────────────────────────────────────── */}
      <div data-invoice-section>
        <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 10 }}>
          Every purchase
        </div>
        {/* On screen, cap at 600px with internal scroll. Print stylesheet
            lifts the cap so all rows print on multi-page reports. */}
        <div
          className="rounded-lg overflow-hidden every-purchase-table"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', maxHeight: 600, overflowY: 'auto' }}
        >
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-subtle)' }}>
                {['Date','Vendor','Qty','$/case','$/lb','Total'].map((h, i) => (
                  <th key={h}
                    className="px-3 py-2"
                    style={{
                      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
                      color: 'var(--text-secondary)',
                      borderBottom: '1px solid var(--border)',
                      textAlign: i >= 2 ? 'right' : 'left',
                      position: 'sticky', top: 0, zIndex: 1,
                    }}
                  >{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id} style={{ borderBottom: i < lines.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmtDateShort(l.invoice_date)}</td>
                  <td className="px-3 py-2 text-sm truncate" style={{ color: 'var(--text-secondary)' }}>{fmtVendor(l.vendor_name)}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{Number(l.quantity || 0)}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '\u2014'}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{l.price_per_lb != null ? `$${Number(l.price_per_lb).toFixed(2)}` : '\u2014'}</td>
                  <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', fontWeight: 500, textAlign: 'right' }}>{l.line_total != null ? fmtMoneyExact(l.line_total) : '\u2014'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Print footer */}
      <div className="print-footer text-xs italic mt-6" style={{ color: 'var(--text-muted)' }}>
        {`Posted \u00B7 Generated ${runAt.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
      </div>
    </div>
  );
}

function SummaryRow({ label, value, last }) {
  return (
    <tr style={{ borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-secondary)', width: '40%' }}>{label}</td>
      <td className="px-3 py-2 text-sm" style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{value}</td>
    </tr>
  );
}
